import { z } from 'zod';
import { SENSITIVE_GROUPS, sensitiveEditKey, sensitiveViewKey, type SensitiveGroup } from './sensitiveFields.js';

/**
 * PER-PERSON OVERRIDES of the "Sensitive information" boxes.
 *
 * The role decides the default; a person may carry an exception in either
 * direction. `inherit` follows the role, `allow` adds the permission even if the
 * role lacks it, `deny` removes it even if the role holds it.
 */
export const SENSITIVE_OVERRIDE_VALUES = ['inherit', 'allow', 'deny'] as const;
export type SensitiveOverrideValue = (typeof SENSITIVE_OVERRIDE_VALUES)[number];

const ValueSchema = z.enum(SENSITIVE_OVERRIDE_VALUES);
const GroupSchema = z.object({
  view: ValueSchema.default('inherit'),
  edit: ValueSchema.default('inherit'),
});

export const SensitiveOverridesSchema = z.object({
  bank: GroupSchema.default({}),
  identity: GroupSchema.default({}),
  salary: GroupSchema.default({}),
});
export type SensitiveOverrides = z.infer<typeof SensitiveOverridesSchema>;

export const NO_SENSITIVE_OVERRIDES: SensitiveOverrides = {
  bank: { view: 'inherit', edit: 'inherit' },
  identity: { view: 'inherit', edit: 'inherit' },
  salary: { view: 'inherit', edit: 'inherit' },
};

/** Whatever is stored (null, a stale shape, junk) read as a complete overrides object. */
export function normaliseSensitiveOverrides(raw: unknown): SensitiveOverrides {
  const parsed = SensitiveOverridesSchema.safeParse(raw ?? {});
  return parsed.success ? parsed.data : { bank: { ...NO_SENSITIVE_OVERRIDES.bank }, identity: { ...NO_SENSITIVE_OVERRIDES.identity }, salary: { ...NO_SENSITIVE_OVERRIDES.salary } };
}

/** True when any value departs from `inherit`. */
export function hasSensitiveOverrides(raw: unknown): boolean {
  const o = normaliseSensitiveOverrides(raw);
  return SENSITIVE_GROUPS.some((g) => o[g].view !== 'inherit' || o[g].edit !== 'inherit');
}

/**
 * Role permissions in, effective permissions out (a new set).
 *
 * Precedence, per group — DENY BEATS ALLOW:
 *  1. `view` is held when it is not denied AND (the role holds it, or view is
 *     allowed, or edit is allowed — edit implies view).
 *  2. `edit` is held when it is not denied AND view is held (by rule 1) AND
 *     (the role holds it, or edit is allowed).
 * So denying view removes edit too, even an explicit allow of edit; denying edit
 * alone leaves view as the role has it.
 *
 * The Administrator is subject to these like everyone else: it holds no
 * sensitive box by default, so an Allow is what gives it one. (The way-back-in
 * protection covers every NON-sensitive permission and is not touched here.)
 */
export function applySensitiveOverrides(
  perms: ReadonlySet<string>,
  overrides: unknown
): Set<string> {
  const out = new Set(perms);
  const o = normaliseSensitiveOverrides(overrides);
  for (const g of SENSITIVE_GROUPS as SensitiveGroup[]) {
    const { view, edit } = o[g];
    const viewKey = sensitiveViewKey(g);
    const editKey = sensitiveEditKey(g);
    const viewHeld = view !== 'deny' && (perms.has(viewKey) || view === 'allow' || edit === 'allow');
    const editHeld = edit !== 'deny' && viewHeld && (perms.has(editKey) || edit === 'allow');
    if (viewHeld) out.add(viewKey); else out.delete(viewKey);
    if (editHeld) out.add(editKey); else out.delete(editKey);
  }
  return out;
}
